import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FindingRow } from '../../assessment-ai-scribe.model';

function titleCaseValue(val: string): string {
  if (!val) return val;
  if (!isNaN(Number(val))) return val;
  return val.replace(/\b\w/g, c => c.toUpperCase());
}

const CONFIDENCE_REASON_LABELS: Record<string, string> = {
  UNIT_ASSUMED: 'Unit was assumed from schema',
  UNIT_NOT_MATCHED: 'Unit does not match schema',
  INFERRED_FROM_GRADE: 'Matched via alias or range',
  INFERRED_FROM_CONTEXT: 'Eye inferred from context',
  DUPLICATE_RESOLVED: 'Assigned to avoid duplicate',
  NO_EYE_SPECIFIED: 'No eye was specified',
  OUT_OF_BOUNDS: 'Value is outside normal range',
  SIGN_FUZZY_MATCH: 'Sign name required fuzzy matching',
  VALUE_FUZZY_MATCH: 'Value required fuzzy matching',
  VALUE_MULTI_GRADE: 'Value spans multiple grades',
  CONFLICTING_STATEMENT: 'Contradicts earlier finding',
  PARTIAL_INFORMATION: 'Finding was incomplete',
  COULD_NOT_RESOLVE: 'Value could not be mapped',
  MULTIPLE_VALUES_MENTIONED: 'Multiple values mentioned'
};

@Component({
  selector: 'csi-scribe-finding-card',
  templateUrl: './scribe-finding-card.component.html',
  styleUrls: ['./scribe-finding-card.component.scss']
})
export class ScribeFindingCardComponent {
  @Input() row!: FindingRow;

  @Output() toggleDelete = new EventEmitter<FindingRow>();

  startEdit() {
    this.row.isEditing = true;
  }

  confirmEdit() {
    this.row.isEditing = false;
    this.row.isModified = this.row.editedValue !== this.row.original.normalizedValue;
    this.row.displayValue = this.computeDisplayValue(this.row);
  }

  cancelEdit() {
    this.row.editedValue = this.row.original.normalizedValue;
    this.row.isEditing = false;
    this.row.isModified = false;
    this.row.displayValue = this.computeDisplayValue(this.row);
  }

  restoreOriginal() {
    this.row.editedValue = this.row.original.normalizedValue;
    this.row.isModified = false;
    this.row.displayValue = this.computeDisplayValue(this.row);
  }

  onToggleDelete() {
    this.row.isDeleted = !this.row.isDeleted;
    this.toggleDelete.emit(this.row);
  }

  private computeDisplayValue(row: FindingRow): string {
    const unit = row.original.system_unit;
    const val = titleCaseValue(row.editedValue);
    return unit ? `${val} ${unit}` : val;
  }

  getConfidenceTooltip(): string {
    return (this.row.reasonLabels || []).map(r => this.resolveReasonLabel(r)).join('\n');
  }

  resolveReasonLabel(code: string): string {
    if (code.startsWith('UNIT_NOT_MATCHED:')) {
      return `Unit "${code.split(':')[1]}" does not match schema`;
    }
    return CONFIDENCE_REASON_LABELS[code] || code;
  }

  /**
   * Returns a thermal severity CSS class based on the finding's normalizedValue.
   * Maps grade0–grade4, qualitative severity words, and numeric sign-specific ranges.
   */
  getSeverityClass(row: FindingRow): string {
    const val = (row.editedValue || '').toLowerCase().trim();
    const key = row.original.systemKey || '';

    // Grade-based (grade0–grade4)
    if (val === 'grade0' || val === 'nolidwiper') return 'sev-0';
    if (val === 'grade1') return 'sev-1';
    if (val === 'grade2') return 'sev-2';
    if (val === 'grade3') return 'sev-3';
    if (val === 'grade4') return 'sev-4';

    // Qualitative severity words
    const sevMap: Record<string, string> = {
      none: 'sev-0',
      normal: 'sev-0',
      negative: 'sev-0',
      clear: 'sev-0',
      minimal: 'sev-0',
      trace: 'sev-1',
      mild: 'sev-1',
      weak: 'sev-1',
      cloudy: 'sev-1',
      moderate: 'sev-2',
      'cloudy+debris': 'sev-2',
      severe: 'sev-3',
      positive: 'sev-3',
      strong: 'sev-3',
      thick: 'sev-3',
      'no glands expressible': 'sev-4'
    };
    if (sevMap[val] !== undefined) return sevMap[val];

    // Numeric ranges for specific signs
    const num = parseFloat(val);
    if (!isNaN(num)) {
      if (key.includes('PRESSURE')) {
        if (num <= 21) return 'sev-0';
        if (num <= 25) return 'sev-1';
        if (num <= 30) return 'sev-2';
        return 'sev-3';
      }
      if (key.includes('NITBUT') || key.includes('ITBUT')) {
        if (num >= 10) return 'sev-0';
        if (num >= 7) return 'sev-1';
        if (num >= 4) return 'sev-2';
        return 'sev-3';
      }
      if (key.includes('SCHIRMERS')) {
        if (num > 10) return 'sev-0';
        if (num >= 5) return 'sev-1';
        return 'sev-3';
      }
      if (key.includes('OSMOLARITY')) {
        if (num < 308) return 'sev-0';
        if (num < 316) return 'sev-1';
        if (num < 350) return 'sev-2';
        return 'sev-3';
      }
      if (key.includes('TEAR_MENISCUS')) {
        if (num >= 0.3) return 'sev-0';
        if (num >= 0.2) return 'sev-1';
        return 'sev-3';
      }
    }
    return '';
  }
}
